import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export interface AuditLog {
  id: string;
  orgId: string;
  actorId: string | null;
  actorType: 'USER' | 'API_TOKEN' | 'SYSTEM' | 'RUSTDESK_AGENT';
  action: string;
  targetType: string | null;
  targetId: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  metadata: unknown;
  success: boolean;
  errorMsg: string | null;
  timestamp: string;
  actor: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
}

interface PaginatedAudit {
  data: AuditLog[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AuditFilters {
  page?: number;
  pageSize?: number;
  action?: string;
  actorId?: string;
  targetType?: string;
  targetId?: string;
  fromDate?: string;
  toDate?: string;
  success?: boolean;
}

export function useAuditLogs(filters: AuditFilters) {
  return useQuery({
    queryKey: ['audit', filters],
    queryFn: () =>
      api.get<PaginatedAudit>('/audit', {
        query: {
          page: filters.page ?? 1,
          pageSize: filters.pageSize ?? 50,
          action: filters.action,
          actorId: filters.actorId,
          targetType: filters.targetType,
          targetId: filters.targetId,
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          success: filters.success,
        },
      }),
    placeholderData: (prev) => prev,
  });
}

export function useExportAuditCsv() {
  return useMutation({
    mutationFn: async (filters: AuditFilters & { limit?: number }) => {
      const csv = await api.downloadText('/audit/export', {
        ...filters,
        limit: filters.limit ?? 10000,
      });
      // Trigger browser download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  });
}
